<template>
<div>
    <!-- Modal confirm delete -->
    <modal name="modal-confirm-delete">
        <div class="pb-4 mb-4 border-b border-gray-200">
            <h3 class="text-xl font-bold text-red-600">{{$t('mes_produits.produit.suppression_du_produit')}}</h3>
            <span class="text-sm font-light">{{$t('mes_produits.produit.souhaitez_vous_vraiment_supprimer_ce_produit_?')}}</span>
        </div>
        <span class="block my-1 font-bold text-gray-800"> {{productData.product.marque + ' - ' + (productData.product.libelle || productData.product.model)}}</span>
        <div class="mt-3">
            <template v-if="!modalLoading">
                <button @click="$modal.hide('modal-confirm-delete')" class="px-3 py-2 text-sm font-light duration-100 bg-gray-200 border-gray-200 hover:bg-gray-300 rounded-xl">
                    <i class="mr-1 fas fa-times"></i>{{$t('mes_produits.produit.annuler')}}
                </button>
                <button class="px-3 py-2 text-sm font-light text-red-900 duration-100 bg-red-300 border-red-200 hover:bg-red-400 rounded-xl" @click="deleteProduct" :disabled="modalLoading" test-id="btn-confirm-delete">
                    <i class="mr-1 fas fa-check"></i> {{$t('mes_produits.produit.supprimer')}}
                </button>
            </template>
            <sav-spinner class="ml-4" v-if="modalLoading" :size="10" />
        </div>
    </modal>

    <!-- Modal relance -->
    <modal name="modal-relance">
        <div class="pb-4 mb-4 border-b border-gray-200">
            <h3 class="text-xl font-bold text-blue-600">{{$t('mes_produits.produit.relancer')}}</h3>
            <span class="text-sm font-light">{{$t('mes_produits.produit.souhaitez_vous_vraiment_faire_une_relance_pour_ce_produit_?')}}</span>
        </div>
        <template v-if="!modalLoading">
            <button @click="$modal.hide('modal-relance')" class="px-3 py-2 text-sm font-light duration-100 bg-gray-200 border-gray-200 hover:bg-gray-300 rounded-xl">
                <i class="mr-1 fas fa-times"></i>{{$t('mes_produits.produit.annuler')}}
            </button>
            <button class="px-3 py-2 text-sm font-light text-white duration-100 bg-blue-400 border-blue-200 hover:bg-blue-500 rounded-xl" @click="relanceProduct" :disabled="modalLoading" test-id="btn-confirm-delete">
                <i class="mr-1 fas fa-check"></i> {{$t('mes_produits.produit.valider_la_demande')}}
            </button>
        </template>
        <sav-spinner class="ml-4" v-if="modalLoading" :size="10" />
    </modal>

    <!-- Content -->
    <div class="mt-3">
        <sav-spinner more-class="mb-3" v-if="loading" />
        <template v-if="data">
           <!-- Case number -->
            <h6 class="mt-3 font-bold">{{$t('mes_produits.produit.dossier.numero_de_dossier')}}</h6>
            <span class="text-gray-600">
                <span>{{ caseData.num_case }}</span>
            </span>
            <!-- Etat -->
            <hr class="mt-5">
            <h6 class="mt-3 font-bold">{{$t('mes_produits.produit.dossier.etat_du_dossier')}}</h6>
            <div class="mt-2 bg-gray-100 p-4 rounded-xl">
                <badge-state :state="state" :label="status" :date="dateLastMaj" />
            </div>
            <!-- Date -->
            <hr class="mt-5">
            <template v-if="state != 2 && data.request_date">
                <h6 class="mt-3 font-bold">{{$t('mes_produits.produit.dossier.demande_du')}}</h6>
                <span class="text-gray-600">
                    <span>{{ data.request_date | moment }}</span>
                </span>
            </template>
            <template v-if="state == 2 && data.date_fin">
                <h6 class="mt-3 font-bold">{{$t('mes_produits.produit.dossier.cloture_le')}}</h6>
                <span class="text-gray-600">
                    <span>{{data.date_fin | moment}}</span>
                </span>
            </template>
            <!-- Code panne -->
            <hr class="mt-3">
            <h6 class="mt-3 font-bold">{{$t('mes_produits.produit.dossier.panne')}}</h6>
            <span class="text-gray-600"> {{ data.descript_fault }} </span>
            <!-- Garantie -->
            <template v-if="data.warranty_type">
                <hr class="mt-3">
                <h6 class="mt-3 font-bold">{{$t('mes_produits.produit.dossier.garantie')}}</h6>
                <span class="text-gray-600"> {{ $tt(data.warranty_type) }} </span>
            </template>
            <!-- Documents -->
            <template v-if="data.documents && data.documents.length > 0">
                <hr class="mt-3">
                <h6 class="mt-3 font-bold">{{$t('mes_produits.produit.dossier.documents')}}</h6>
                <div class="mt-1" v-for="(document, docIndex) in data.documents" :key="'doc-' + docIndex">
                    <a target="_blank" class="text-blue-500 hover:underline" :href="document.url">{{$tt(document.type)}}</a>
                </div>
            </template>
            <!-- Timeline -->
            <template v-if="data.statut">
                <hr class="my-3">
                <h6 class="mt-3 font-bold">{{$t('mes_produits.produit.dossier.avancement_du_dossier')}}</h6>
                <ol class="relative mt-3 border-l border-gray-400">
                    <li class="mb-10 ml-4" v-for="(item, indexItem) in data.statut" :key="'case-detail-' + item.id">
                        <div class="absolute w-3 h-3 rounded-full mt-1.5 -left-1.5" :class="isLastStepInModal(indexItem) ? 'bg-sky-500' : 'bg-gray-400'"></div>
                        <time class="mb-1 text-sm font-normal leading-none" :class="isLastStepInModal(indexItem) ? 'text-sky-500' : 'text-gray-600'">Le {{ item.date_av | momentWithHour }}</time>
                        <h3 class="text-lg" :class="isLastStepInModal(indexItem) ? 'font-semibold text-gray-900' : 'text-sm mt-1 font-light text-gray-600'">{{$tt(item.libelle_av)}}</h3>
                        <p v-if="item.comment" class="mb-4 text-base">{{ $tt(item.comment) }}</p>
                    </li>
                </ol>
            </template>
            <!-- Actions -->
            <hr class="mt-3">
            <h6 class="mt-3 font-bold">{{$t('mes_produits.produit.actions')}}</h6>
            <!-- Delete -->
            <button v-if="caseData.remove == 1" @click="displayDeleteModal" class="px-3 py-2 mt-2 text-sm font-light text-white duration-100 bg-red-400 hover:bg-red-500 rounded-xl" test-id="btn-delete-product">
                <small>
                    <i class="mr-1 fas fa-times"></i> {{$t('mes_produits.produit.supprimer')}}
                </small>
            </button>
            <!-- Relance -->
            <button @click="caseData.relance == 0 ? $toast.warning('Vous ne pouvez pas relancer maintenant') : displayRelanceModal" class="px-3 py-2 mt-2 text-sm font-light text-white duration-100 rounded-xl" :class="caseData.relance == 0 ? 'bg-blue-300 cursor-no-drop' : 'bg-blue-400 hover:bg-blue-500'" test-id="btn-delete-product">
                <small>
                    <i class="mr-1 fas fa-circle-question"></i> {{$t('mes_produits.produit.relancer')}}
                </small>
            </button>
        </template>
    </div>
</div>
</template>

<script>
import apiService from '@/services/api.service'
import eventHub from '@/eventHub'

import moment from 'moment'
import BadgeState from './BadgeState.vue'

export default {
  components: { BadgeState },
    props: {
        id: {
            required: true
        },
        state: {
            required: true
        },
        status: {
            required: true
        },
        dateLastMaj: {},
        caseData: {},
        productData: {}
    },

    data() {
        return {
            open: false,
            loading: false,
            data: null,
            modalLoading: false,
        }
    },

    computed: {
        productName() {
            if (this.data.device && this.data.device.brand) {
                return this.data.device.model + ' ' + this.data.device.brand
            }
        }
    },

    filters: {
        moment(date) {
            return moment(date).format('DD/MM/YYYY');
        },

        momentWithHour(date) {
            return moment(date).lang('fr').format('DD/MM/YYYY à HH:mm');
        }
    },

    methods: {
        async getDetailCase(id) {
            this.loading = true
            let response = await apiService.post('getdetailcase', {
                id_case: id
            })
            this.data = response.data.posts.post

            this.loading = false
        },

        isLastStepInModal(index) {
            if (this.data) {
                return index + 1 == this.data.statut.length
            }
        },

        dateFormat(date) {
            return moment(date).format('DD/MM/YYYY'); 
        },

        displayRelanceModal() {
            this.$modal.show('modal-relance')
        },

        async relanceProduct() {
            this.modalLoading = true
            await apiService.post('relance', {
                id_case: this.id
            })
            eventHub.$emit('refresh-product')
            this.$modal.hide('modal-relance')
            this.$toast.success(this.$t('mes_produits.produit.relance_envoyee'))
            this.modalLoading = false
        },

        displayDeleteModal() {
            this.$modal.show('modal-confirm-delete')
        },

        async deleteProduct() {
            this.modalLoading = true
            await apiService.post('deleterequest', {
                id_case: this.id
            })
            this.$modal.hide('modal-confirm-delete')
            eventHub.$emit('refresh-product')
            this.modalLoading = false
        },
    },

    mounted() {
        this.getDetailCase(this.id)
    }
}
</script>



// WEBPACK FOOTER //
// src/companies/default/views/user/product/FolderCardContent.vue